import MenuIcon from '@mui/icons-material/Menu'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import KitchenRoundedIcon from '@mui/icons-material/KitchenRounded'
import LocalDiningRoundedIcon from '@mui/icons-material/LocalDiningRounded'
import RestaurantMenuRoundedIcon from '@mui/icons-material/RestaurantMenuRounded'
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded'
import TableRestaurantRoundedIcon from '@mui/icons-material/TableRestaurantRounded'
import TakeoutDiningRoundedIcon from '@mui/icons-material/TakeoutDiningRounded'
import {
  Box,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  alpha,
} from '@mui/material'
import { useTheme, type Theme } from '@mui/material/styles'
import React, { useEffect, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ITables } from '../all-types'
import { CommonBtn, PrinterStatusHeader } from '../components'
import SearchInput from '../components/Input/SearchInput/SearchInput'
import {
  deliveryOptions,
  MODIFY_BILL_TABLE_ID,
  TAKE_AWAY_TABLE_ID,
} from '../constants'
import {
  EffectiveConnectionType,
  usePrinterSettings,
  useProductSearch,
} from '../hooks'
import { setSelectedFloor, setSelectedTable } from '../redux-store'
import { useAppDispatch, useAppSelector } from '../redux-store/hooks'
import { ConnectionStatus } from '../screens'
import { unlockTable, updateCartItemOfTheTable } from '../services'

interface AppHeaderControlsProps {
  handleDrawerOpen: () => void
  variant?: 'app' | 'titlebar'
  showMenuButton?: boolean
  showRightExtras?: boolean
}

export const getNetworkHeaderColor = (
  theme: Theme,
  isOnline: boolean,
  effectiveType: EffectiveConnectionType,
  baseGlassColor: string
) => {
  if (!isOnline) {
    return alpha(theme.palette.error.main, 0.15)
  }

  switch (effectiveType) {
    case '4g':
      return baseGlassColor
    case '3g':
      return alpha(theme.palette.warning.main, 0.1)
    case '2g':
    case 'slow-2g':
      return alpha(theme.palette.error.main, 0.15)
    default:
      return baseGlassColor
  }
}

const TabButton = ({
  active,
  children,
  icon,
  onClick,
}: {
  active: boolean
  children: React.ReactNode
  icon: React.ReactNode
  onClick: () => void
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const accentColor = isDark
    ? theme.palette.primary.main
    : theme.palette.secondary.main

  return (
    <CommonBtn
      size="small"
      startIcon={icon}
      onClick={onClick}
      sx={{
        minHeight: 48,
        px: 2.5,
        py: 1.05,
        borderRadius: '14px',
        textTransform: 'none',
        fontSize: '1rem',
        fontWeight: active ? 700 : 500,
        letterSpacing: 0,
        boxShadow: active
          ? `0 10px 24px ${alpha(accentColor, isDark ? 0.24 : 0.2)}`
          : 'none',
        bgcolor: active ? accentColor : 'transparent',
        color: active
          ? theme.palette.getContrastText(accentColor)
          : theme.palette.text.secondary,
        transition: 'all 0.18s ease',
        '& .MuiButton-startIcon': { mr: 0.95 },
        '& .MuiSvgIcon-root': { fontSize: 21 },
        '&:hover': {
          bgcolor: active
            ? accentColor
            : alpha(theme.palette.text.primary, isDark ? 0.12 : 0.07),
          color: active
            ? theme.palette.getContrastText(accentColor)
            : theme.palette.text.primary,
          transform: 'translateY(-1px)',
        },
      }}
    >
      {children}
    </CommonBtn>
  )
}

const SearchContainer = ({
  children,
  compact,
}: {
  children: React.ReactNode
  compact: boolean
}) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'
  const accentColor = isDark
    ? theme.palette.primary.main
    : theme.palette.secondary.main

  return (
    <Box
      sx={{
        flex: 1,
        maxWidth: compact ? 420 : 500,
        ml: compact ? 1 : 2,
        '& .MuiOutlinedInput-root': {
          minHeight: compact ? 46 : 54,
          fontSize: '1.02rem',
          backgroundColor: alpha(
            theme.palette.background.paper,
            isDark ? 0.42 : 0.82
          ),
          borderRadius: '14px',
          transition: 'all 0.18s ease',
          '& fieldset': {
            borderColor: alpha(theme.palette.divider, isDark ? 0.18 : 0.22),
          },
          '&:hover': {
            backgroundColor: alpha(
              theme.palette.background.paper,
              isDark ? 0.54 : 1
            ),
            '& fieldset': { borderColor: alpha(accentColor, 0.5) },
          },
          '&.Mui-focused': {
            backgroundColor: alpha(
              theme.palette.background.paper,
              isDark ? 0.8 : 1
            ),
            boxShadow: `0 0 0 3px ${alpha(accentColor, 0.18)}`,
            '& fieldset': { borderColor: accentColor },
          },
        },
      }}
    >
      {children}
    </Box>
  )
}

const AppHeaderControls: React.FC<AppHeaderControlsProps> = ({
  handleDrawerOpen,
  variant = 'app',
  showMenuButton = true,
  showRightExtras = true,
}) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const queryParams = new URLSearchParams(location.search)
  const isDark = theme.palette.mode === 'dark'
  const compact = variant === 'titlebar'
  const accentColor = isDark
    ? theme.palette.primary.main
    : theme.palette.secondary.main
  const accentContrast = theme.palette.getContrastText(accentColor)

  const glassBorder = `1px solid ${alpha(isDark ? theme.palette.common.white : theme.palette.common.black, isDark ? 0.12 : 0.08)}`
  const tab = queryParams.get('tab') || 'ongoing'
  const isTableView = tab === 'table'
  const isOngoingOrders = tab === 'ongoing'
  const isNewTakeAway = tab === 'new-take-away'
  const isModifyBillView = tab === 'modify-bill'

  const allFloors = useAppSelector((state) => state.preloadData.floors)
  const selectedFloor = useAppSelector(
    (state) => state.preloadData.selectedFloor
  )
  const selectedTable = useAppSelector((state) => state.table.selectedTable)
  const userId = useAppSelector((state) => state.authentication.userId)

  const isCounterRoute = location.pathname === '/app/counter'
  const isSettingTab = location.pathname === '/app/settings'
  const isKitchenRoute = location.pathname === '/app/kitchen'
  const isHistoryRoute = location.pathname === '/app/history'

  const isTakeAwayTable = selectedTable?._id === TAKE_AWAY_TABLE_ID
  const isModifyBillTable = selectedTable?._id === MODIFY_BILL_TABLE_ID
  const showFloorSelector = isCounterRoute && !selectedTable
  const showtableInfo = Boolean(
    isCounterRoute &&
      selectedTable &&
      (isTableView || isModifyBillView || (isNewTakeAway && isTakeAwayTable))
  )
  const showTakeAway = Boolean(isCounterRoute && isNewTakeAway && selectedTable)
  const showProductSearch =
    isCounterRoute && !((!showtableInfo || isOngoingOrders) && !isTakeAwayTable)

  const { printerStatus } = usePrinterSettings()
  const { searchTxt, searchTheProduct } = useProductSearch(showProductSearch)
  const routeTitle = useMemo(() => {
    if (isSettingTab) return { label: 'Settings', Icon: SettingsRoundedIcon }
    if (isHistoryRoute) return { label: 'History', Icon: HistoryRoundedIcon }
    return { label: 'Kitchen', Icon: KitchenRoundedIcon }
  }, [isHistoryRoute, isSettingTab])
  const RouteIcon = routeTitle.Icon

  const handleFloorChange = (event: SelectChangeEvent) => {
    const floorId = event.target.value
    const floor = allFloors.find((f) => f._id === floorId)
    if (floor) dispatch(setSelectedFloor(floor))
  }

  useEffect(() => {
    if (allFloors && allFloors.length > 0 && isTableView && !selectedFloor) {
      dispatch(setSelectedFloor(allFloors[0]))
    }
  }, [allFloors, dispatch, isTableView, selectedFloor])

  const closeTable = () => {
    // Snappy UX: navigate back to the floor view immediately so the user
    // gets instant feedback. Unlock + any cleanup happens in the
    // background — previously we awaited those network calls before
    // hiding the products view, which made closing feel laggy (1-2s).
    const tableSnapshot = selectedTable
    dispatch(setSelectedTable(null))

    if (!tableSnapshot?._id) return

    void (async () => {
      try {
        if (
          tableSnapshot.billSession &&
          tableSnapshot.billSession.items?.length === 0
        ) {
          const clonedTable: ITables = JSON.parse(JSON.stringify(tableSnapshot))
          clonedTable.billSession = null
          await dispatch(updateCartItemOfTheTable({ table: clonedTable }))
        }
      } catch (e) {
        console.error(e)
      }
      try {
        if (userId) {
          await dispatch(
            unlockTable({ tableId: tableSnapshot._id ?? '', lockedBy: userId })
          )
        }
      } catch (e) {
        console.error(e)
      }
    })()
  }

  return (
    <>
      {showMenuButton && (
        <IconButton
          onClick={handleDrawerOpen}
          edge="start"
          aria-label="Open menu"
          sx={
            {
              mr: compact ? 1.5 : 2.5,
              ml: compact ? 0 : undefined,
              width: compact ? 38 : 48,
              height: compact ? 38 : 48,
              color: accentColor,
              bgcolor: alpha(accentColor, isDark ? 0.16 : 0.1),
              backdropFilter: 'blur(14px)',
              border: `1px solid ${alpha(accentColor, 0.24)}`,
              boxShadow: `0 10px 24px ${alpha(accentColor, isDark ? 0.16 : 0.1)}`,
              transition: 'all 0.18s ease',
              WebkitAppRegion: 'no-drag',
              '&:hover': {
                bgcolor: alpha(accentColor, 0.18),
                transform: 'translateY(-1px)',
              },
            } as any
          }
        >
          <MenuIcon fontSize={compact ? 'small' : 'medium'} />
        </IconButton>
      )}

      <Grid
        container
        alignItems="center"
        wrap="nowrap"
        sx={{ minWidth: 0, WebkitAppRegion: 'no-drag' } as any}
      >
        {(isSettingTab || isKitchenRoute || isHistoryRoute) && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.75}
            sx={{ ml: compact ? 0 : 1, minWidth: 0 }}
          >
            <Box
              sx={{
                alignItems: 'center',
                bgcolor: alpha(accentColor, isDark ? 0.18 : 0.12),
                border: `1px solid ${alpha(accentColor, 0.24)}`,
                borderRadius: '14px',
                color: accentColor,
                display: 'flex',
                height: compact ? 42 : 50,
                justifyContent: 'center',
                width: compact ? 42 : 50,
              }}
            >
              <RouteIcon sx={{ fontSize: compact ? 22 : 26 }} />
            </Box>
            <Typography
              variant={compact ? 'h6' : 'h5'}
              noWrap
              sx={{
                fontWeight: 900,
                color: 'text.primary',
                letterSpacing: 0,
                fontSize: compact ? '1.2rem' : '1.4rem',
              }}
            >
              {routeTitle.label}
            </Typography>
            {isKitchenRoute && (
              <Typography
                variant="caption"
                noWrap
                sx={{
                  bgcolor: alpha(accentColor, 0.14),
                  color: accentColor,
                  px: 1.5,
                  py: 0.7,
                  borderRadius: '10px',
                  fontWeight: 900,
                  fontSize: '0.78rem',
                }}
              >
                EXPRESS (FAST)
              </Typography>
            )}
          </Stack>
        )}

        {isCounterRoute && (
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              minWidth: 0,
            }}
          >
            {!showProductSearch ? (
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  bgcolor: alpha(
                    theme.palette.text.primary,
                    isDark ? 0.1 : 0.06
                  ),
                  p: 0.6,
                  borderRadius: '18px',
                  border: glassBorder,
                  boxShadow: `inset 0 1px 0 ${alpha(theme.palette.common.white, isDark ? 0.05 : 0.48)}`,
                  minWidth: 'max-content',
                }}
              >
                <TabButton
                  active={isOngoingOrders}
                  icon={<RestaurantMenuRoundedIcon />}
                  onClick={() => navigate('/app/counter?tab=ongoing')}
                >
                  Ongoing
                </TabButton>
                <TabButton
                  active={isTableView}
                  icon={<TableRestaurantRoundedIcon />}
                  onClick={() => navigate('/app/counter?tab=table')}
                >
                  Dine-In
                </TabButton>
                <TabButton
                  active={isNewTakeAway}
                  icon={<TakeoutDiningRoundedIcon />}
                  onClick={() => {
                    if (selectedTable && !isTakeAwayTable) {
                      dispatch(setSelectedTable(null))
                    }
                    navigate('/app/counter?tab=new-take-away')
                  }}
                >
                  Take-Away
                </TabButton>
              </Stack>
            ) : (
              <SearchContainer compact={compact}>
                <SearchInput
                  value={searchTxt}
                  onChange={(txt) => searchTheProduct(txt)}
                  placeholder="Search Name, ID, or Category..."
                />
              </SearchContainer>
            )}
          </Box>
        )}

        <Stack
          direction="row"
          alignItems="center"
          spacing={compact ? 1.25 : 2.5}
          sx={{ ml: 'auto', minWidth: 0 }}
        >
          {isTableView && showFloorSelector && (
            <FormControl
              variant="outlined"
              size="small"
              sx={{ minWidth: compact ? 130 : 158 }}
            >
              <InputLabel
                id="floor-select-label"
                sx={{ top: -2, fontSize: '1rem' }}
              >
                Floor
              </InputLabel>
              <Select
                labelId="floor-select-label"
                value={selectedFloor?._id ?? ''}
                onChange={handleFloorChange}
                label="Floor"
                MenuProps={{
                  sx: { zIndex: 13050 },
                  PaperProps: {
                    sx: { zIndex: 13050 },
                  },
                }}
                sx={{
                  borderRadius: '20px',
                  bgcolor: alpha(
                    theme.palette.background.paper,
                    isDark ? 0.4 : 0.6
                  ),
                  backdropFilter: 'blur(10px)',
                  '.MuiOutlinedInput-notchedOutline': {
                    borderColor: alpha(theme.palette.divider, 0.15),
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: accentColor,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: accentColor,
                  },
                }}
              >
                {allFloors.map((floor) => (
                  <MenuItem
                    key={floor._id}
                    value={floor._id}
                    sx={{ borderRadius: '10px', mx: 1, my: 0.5 }}
                  >
                    Floor {floor.floorNo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {(showtableInfo || (showTakeAway && isTakeAwayTable)) && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={compact ? 1.25 : 2}
              sx={{
                bgcolor: alpha(accentColor, 0.12),
                border: `1px solid ${alpha(accentColor, 0.25)}`,
                px: compact ? 1.25 : 1.5,
                py: compact ? 0.7 : 0.9,
                borderRadius: '18px',
                backdropFilter: 'blur(20px)',
                boxShadow: `0 12px 28px ${alpha(accentColor, isDark ? 0.18 : 0.1)}`,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                minWidth={0}
              >
                {!isTakeAwayTable && !isModifyBillTable && (
                  <LocalDiningRoundedIcon
                    sx={{ color: accentColor, fontSize: compact ? 22 : 25 }}
                  />
                )}
                {isTakeAwayTable && selectedTable.billSession?.takeAwayType && (
                  <Box
                    component="img"
                    src={
                      deliveryOptions[selectedTable.billSession?.takeAwayType]
                        ?.logoUrl
                    }
                    sx={{
                      width: 26,
                      height: 26,
                      borderRadius: '50%',
                      bgcolor: 'white',
                      p: 0.5,
                    }}
                  />
                )}
                <Typography
                  variant="subtitle1"
                  noWrap
                  sx={{
                    fontWeight: 900,
                    color: accentColor,
                    letterSpacing: 0,
                    fontSize: compact ? '1rem' : '1.1rem',
                  }}
                >
                  {isModifyBillTable
                    ? `Edit Bill ${selectedTable.billSession?.billNo || ''}`
                    : isTakeAwayTable
                    ? selectedTable.billSession?.takeAwayType
                    : `Table ${selectedTable?.tableNo}`}
                </Typography>
              </Stack>
              <Divider
                orientation="vertical"
                flexItem
                sx={{ borderColor: alpha(accentColor, 0.3) }}
              />
              <IconButton
                size="small"
                onClick={
                  isTakeAwayTable
                    ? () => dispatch(setSelectedTable(null))
                    : isModifyBillTable
                      ? () => {
                          dispatch(setSelectedTable(null))
                          navigate('/app/history')
                        }
                    : closeTable
                }
                sx={{
                  color: accentColor,
                  bgcolor: alpha(accentColor, 0.15),
                  width: 30,
                  height: 30,
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: accentColor,
                    color: accentContrast,
                    transform: 'scale(1.06)',
                  },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          )}

          {showRightExtras && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={compact ? 1 : 1.5}
              sx={
                {
                  ml: compact ? 0.5 : 1,
                  pl: compact ? 1.5 : 2.5,
                  borderLeft: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  display: { xs: 'none', md: 'flex' },
                  WebkitAppRegion: 'no-drag',
                } as any
              }
            >
              <ConnectionStatus compact />
              <PrinterStatusHeader
                printerStatus={printerStatus}
                compact={isCounterRoute || isKitchenRoute || compact}
              />
            </Stack>
          )}
        </Stack>
      </Grid>
    </>
  )
}

export default AppHeaderControls
